import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/CloudQueue';
import DeviceIcon from '@mui/icons-material/PhoneAndroid';
import { imageDbService } from '../../services/imageDbService';
import { downloadImageFromDrive } from '../../services/googleDriveImageService';
import { syncService } from '../../services/syncService';
import { warbandMemberImageService } from '../../services/warbandMemberImageService';
import { useAuthStore } from '../../stores/authStore';

const PortraitPreview: React.FC<{ blob: Blob | null; label: string }> = ({ blob, label }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const nextUrl = URL.createObjectURL(blob);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [blob]);

  return (
    <Stack alignItems="center" spacing={1} flex={1}>
      <Avatar variant="rounded" src={url ?? undefined} sx={{ width: 144, height: 144 }}>
        None
      </Avatar>
      <Typography variant="subtitle2">{label}</Typography>
    </Stack>
  );
};

export const ImageConflictResolutionModal: React.FC = () => {
  const accessToken = useAuthStore((state) => state.accessToken);
  const conflicts = useAuthStore((state) => state.pendingImageConflicts);
  const setConflicts = useAuthStore((state) => state.setPendingImageConflicts);
  const setSyncStatus = useAuthStore((state) => state.setSyncStatus);
  const [localBlob, setLocalBlob] = useState<Blob | null>(null);
  const [remoteBlob, setRemoteBlob] = useState<Blob | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const conflict = conflicts[0];

  useEffect(() => {
    let cancelled = false;
    if (!conflict || !accessToken) return;
    void Promise.all([
      imageDbService.loadImage(conflict.warbandId, conflict.memberId),
      conflict.remoteDriveImageId
        ? downloadImageFromDrive(accessToken, conflict.remoteDriveImageId)
        : Promise.resolve(null),
    ]).then(([local, remote]) => {
      if (!cancelled) {
        setLocalBlob(local);
        setRemoteBlob(remote);
      }
    });
    return () => { cancelled = true; };
  }, [accessToken, conflict]);

  const resolve = async (choice: 'local' | 'drive') => {
    if (!conflict || !accessToken) return;
    setIsResolving(true);
    setSyncStatus('syncing');
    try {
      if (choice === 'local') {
        await warbandMemberImageService.resolveConflictWithLocal(conflict, accessToken);
      } else {
        await warbandMemberImageService.resolveConflictWithDrive(conflict, accessToken);
      }
      const result = await syncService.syncWarband(
        conflict.warbandId,
        accessToken,
        choice === 'local'
      );
      const remaining = [...result.imageConflicts, ...conflicts.slice(1)];
      setConflicts(remaining);
      setSyncStatus(remaining.length > 0 ? 'idle' : 'success');
    } catch (error) {
      setSyncStatus('error', error instanceof Error ? error.message : 'Image sync failed');
    } finally {
      setIsResolving(false);
    }
  };

  if (!conflict || !accessToken) return null;

  return (
    <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown={isResolving}>
      <DialogTitle>Portrait conflict</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {conflict.memberName}'s portrait changed both on this device and on Drive. Choose which one to keep.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <PortraitPreview blob={localBlob} label="This device" />
          <PortraitPreview blob={remoteBlob} label="Google Drive" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {isResolving && <CircularProgress size={20} sx={{ mr: 'auto' }} />}
        <Button startIcon={<CloudIcon />} onClick={() => void resolve('drive')} disabled={isResolving}>
          Use Drive
        </Button>
        <Button variant="contained" startIcon={<DeviceIcon />} onClick={() => void resolve('local')} disabled={isResolving}>
          Use this device
        </Button>
      </DialogActions>
    </Dialog>
  );
};